import React, { useEffect, useState } from "react";
import { Mail, CheckCircle, Clock, Search, Filter, ShieldCheck, ArrowLeft, Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function InquiriesList() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const navigate = useNavigate();
  
  const base = process.env.REACT_APP_API_BASE || "";

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${base}/inquiries`);
      const data = await res.json();
      if (data.success) {
        setInquiries(data.inquiries || []);
      }
    } catch (err) {
      console.error("Failed fetching inquiries ledger:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  const handleToggleStatus = async (id, currentStatus) => {
    const nextStatus = currentStatus === "pending" ? "contacted" : "pending";
    try {
      const res = await fetch(`${base}/inquiries/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: nextStatus })
      });
      const data = await res.json();
      if (data.success) {
        setInquiries(prev => 
          prev.map(item => item.id === id ? { ...item, status: nextStatus } : item)
        );
      }
    } catch (err) {
      console.error("Failed changing interactive client status flag:", err);
    }
  };

  const filteredInquiries = inquiries.filter(item => {
    const searchString = searchTerm.toLowerCase();
    
    // Defensive normalization checks for database records
    const name = item.name || "";
    const email = item.email || "";
    const phone = item.phone || item.PHONE || ""; 
    const message = item.message || "";

    const matchesSearch = 
      name.toLowerCase().includes(searchString) || 
      email.toLowerCase().includes(searchString) ||
      phone.toLowerCase().includes(searchString) ||
      message.toLowerCase().includes(searchString);
    
    const matchesStatus = statusFilter === "all" || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="p-6 bg-[#F4F6FA] min-h-screen">
      {/* Upper Navigation Row Control */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <button 
            onClick={() => navigate("/")} 
            className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-[#194393] mb-2 transition-colors"
          >
            <ArrowLeft size={14} /> Back to Dashboard
          </button>
          <h2 className="text-xl font-black text-[#194393] tracking-tight">Client Inquiries Register</h2>
          <p className="text-xs text-slate-400 font-medium mt-0.5">
            Manage live communications generated from the frontend Contact Us page.
          </p>
        </div>

        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full text-emerald-600">
          <ShieldCheck size={14} />
          <span className="text-[10px] font-bold uppercase tracking-wider">Audit Logs Secure</span>
        </div>
      </div>

      {/* Search & Interaction Filter Banner */}
      <div className="bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="relative w-full md:max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            type="text"
            placeholder="Search by name, email, phone, context string..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 text-slate-700 text-sm rounded-xl focus:outline-none focus:ring-2 focus:ring-[#194393]/20 focus:bg-white transition-all"
          />
        </div>

        <div className="flex items-center gap-2 w-full md:w-auto justify-end">
          <Filter size={14} className="text-slate-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-slate-50 border border-slate-200 text-slate-700 text-xs font-bold p-2.5 rounded-xl focus:outline-none cursor-pointer"
          >
            <option value="all">Show All Records</option>
            <option value="pending">⏳ Status: Pending Only</option>
            <option value="contacted">✅ Status: Contacted Only</option>
          </select>
        </div>
      </div>

      {/* Main Card Render List Stack */}
      {loading ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center text-slate-400 font-bold text-xs">
          Compiling remote database pipelines...
        </div>
      ) : filteredInquiries.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center text-slate-400 font-bold text-xs">
          No records discovered matching your validation filters.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredInquiries.map((inq) => {
            // Defensive extraction variable mapping
            const currentPhone = inq.phone || inq.PHONE;

            return (
              <div 
                key={inq.id} 
                className={`p-5 rounded-2xl bg-white border shadow-sm flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 transition-all duration-200
                  ${inq.status === 'contacted' ? 'border-emerald-100 bg-emerald-50/5' : 'border-slate-200/70 hover:border-slate-300'}`}
              >
                <div className="space-y-3 flex-grow max-w-4xl">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <div className="h-8 w-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
                      <Mail size={15} />
                    </div>
                    <span className="font-black text-sm text-slate-800">{inq.name}</span>
                    <a href={`mailto:${inq.email}`} className="text-xs text-slate-400 font-medium hover:underline">{inq.email}</a>
                    
                    {/* Clickable Dialer Contact Badge with visual placeholder if key empty */}
                    {currentPhone ? (
                      <a 
                        href={`tel:${currentPhone}`} 
                        className="text-xs text-[#194393] font-bold bg-blue-50/70 hover:bg-blue-100/70 border border-blue-200/40 px-2.5 py-0.5 rounded-md transition-all inline-flex items-center gap-1"
                      >
                        <Phone size={11} /> {currentPhone}
                      </a>
                    ) : (
                      <span className="text-xs text-slate-400 italic bg-slate-100 border border-slate-200/60 px-2 py-0.5 rounded-md inline-flex items-center gap-1">
                        <Phone size={11} /> No Phone Provided
                      </span>
                    )}
                    
                    {inq.company && <span className="text-[10px] font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md uppercase tracking-wide">{inq.company}</span>}
                    {inq.service && <span className="text-[10px] font-bold bg-amber-50 text-[#FF9E1B] px-2 py-0.5 rounded-md uppercase tracking-wide">{inq.service}</span>}
                  </div>
                  
                  <p className="text-xs text-slate-600 bg-slate-50/80 p-3 rounded-xl border border-slate-200/50 leading-relaxed font-medium">
                    {inq.message}
                  </p>

                  <div className="text-[10px] text-slate-400 font-semibold flex items-center gap-1">
                    <Clock size={12} /> Log Timestamp: {new Date(inq.created_at).toLocaleString()}
                  </div>
                </div>

                {/* Status Action Block alignment controls */}
                <div className="w-full lg:w-auto shrink-0 border-t lg:border-t-0 pt-3 lg:pt-0 flex justify-end">
                  <button
                    onClick={() => handleToggleStatus(inq.id, inq.status)}
                    className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-black transition-all tracking-wide border w-full lg:w-auto justify-center shadow-sm active:scale-[0.99]
                      ${inq.status === "contacted" 
                        ? "bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100" 
                        : "bg-amber-50 text-[#FF9E1B] border-amber-200 hover:bg-amber-100"}`}
                  >
                    {inq.status === "contacted" ? (
                      <><CheckCircle size={14} /> Contacted</>
                    ) : (
                      <><Clock size={14} /> Mark Contacted</>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}